import styles from "../styles/SubscribeCart.module.scss";

/** Current tax rate of the liberal products. */
const TAX_RATE = 0.2;

const calcPricesFromBaseAmount = (baseAmount, taxRate) => ({
  default_price: baseAmount,
  localized_price: new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(baseAmount / 100),
  localized_price_subtaxes: new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(baseAmount / 100 - (baseAmount / 100) * taxRate),
  localized_price_taxes: new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format((baseAmount / 100) * taxRate),
});

/** Return the Stripe product, based on the current number of thematics.
 *
 * @param {Object} products - The Stripe products. Need an object of format { [NbThematics]: Product }
 * @param {Array} thematics - The currently selected thematics (array of thematic ids)
 * @param {Object} currentProduct - The currently selected product, as { ...Product }
 * @param {Boolean} isAdding - If the user is adding or removing a thematic. You can only step up the Stripe product, not step down.
 * @returns {Object | null} The Stripe product, or null if no product is found.
 */
export const getStripeProductFromDiseases = (products, diseases, currentProduct = {}, isAdding = true) => {
  // If a product is already selected and we removed a thematic, return the current product.
  if (currentProduct?.id && !isAdding) return currentProduct;

  const nbOfDiseasesSelected = (diseases ?? []).length === 0 ? Object.keys(products)[0] : diseases.length;
  return products[nbOfDiseasesSelected] ?? null;
};

/** Format the Stripe product to a more readable format for the front-end. */
export const formatStripeProduct = (product, discount) => {
  if (!product) return {};
  const { id, name, prices, metadata } = product;
  const price_unit = prices.data[0].unit_amount_decimal;
  return {
    id,
    name,
    ...calcPricesFromBaseAmount(price_unit, TAX_RATE),
    NbThematics: metadata.NbThematics,
    NbPatients: metadata.NbPatients,
  };
};

/** Apply a discount to a FORMATTED product object.
 * 
 * > *Note:* This function some JSX, so it should be used in a React component, and should not be saved in local storage 
 * or sent to the backend. Use this purely for UI.
 *
 * @param {Object} product - The product object to apply the discount to. Should be formatted using `formatStripeProduct`.
 * @param {Object} discount - The discount object to apply.
 */
export const applyDiscount = (product, discount = {}) => {
  const { discount_code, amount_off, percent_off } = discount;
  if (!product || !discount_code) return product;

  // The formatting is almost the same, with the old price crossed out, and the new price.
  const { default_price } = product;

  const new_default_price = amount_off ? default_price - amount_off : default_price * (1 - percent_off / 100);
  const { localized_price, localized_price_subtaxes, localized_price_taxes } = calcPricesFromBaseAmount(new_default_price, TAX_RATE);

  return {
    ...product,
    default_price: new_default_price,
    localized_price: (
      <span className={styles.discount}>
        <s>{product.localized_price}</s>
        {localized_price}
      </span>
    ),
    localized_price_subtaxes: (
      <span className={styles.discount}>
        <s>{product.localized_price_subtaxes}</s>
        {localized_price_subtaxes}
      </span>
    ),
    localized_price_taxes: (
      <span className={styles.discount}>
        <s>{product.localized_price_taxes}</s>
        {localized_price_taxes}
      </span>
    ),
  };
};
